import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import { Camera, X, Eye, Info } from "lucide-react";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/jpg"];

// ─── Slot Definitions ─────────────────────────────────────────────────────────

type SlotKey = "front" | "back" | "sizeTag" | "damage" | "side" | "additional";

type Slot = {
    key: SlotKey;
    label: string;
    required: boolean;
    sampleImage?: string;
    tooltip: string;
};

const SLOTS: Slot[] = [
    {
        key: "front",
        label: "Front View",
        required: true,
        sampleImage: "/front-view.jpeg",
        tooltip: "Upload a clear photo facing the front of both shoes together.",
    },
    {
        key: "back",
        label: "Back View",
        required: true,
        sampleImage: "/back-view.jpeg",
        tooltip: "Upload a photo showing the rear heels of both shoes clearly.",
    },
    {
        key: "sizeTag",
        label: "Size / Brand Tag",
        required: true,
        sampleImage: "/sku-tag.jpeg",
        tooltip: "Take a sharp, close-up photo of the inner size and brand tag.",
    },
    {
        key: "damage",
        label: "Damage Close-Up",
        required: true,
        sampleImage: "/damage-close-up.jpeg",
        tooltip: "Provide a clear, zoomed-in photo focused directly on the required service area.",
    },
    {
        key: "side",
        label: "Side View",
        required: false,
        sampleImage: "/side-view.jpeg",
        tooltip: "Upload a profile photo showcasing the full outer side of both shoes.",
    },
    {
        key: "additional",
        label: "Additional Angle",
        required: false,
        sampleImage: "/additional-angle.jpeg",
        tooltip: "Provide any extra perspective photo that helps verify your service request.",
    },
];

const REQUIRED_COUNT = SLOTS.filter((s) => s.required).length; // 4
const TOTAL_COUNT = SLOTS.length; // 6

// ─── Types ────────────────────────────────────────────────────────────────────

interface UploadImageProps {
    onFilesChange: (files: File[]) => void;
    acceptedSlotImages?: (string | null)[];
    onAcceptedSlotImagesChange?: (urls: (string | null)[]) => void;
}

type SlotState = {
    file: File | null;
    preview: string | null;
    isAcceptedUrl?: boolean;
};

// ─── Tooltip Component ────────────────────────────────────────────────────────

const Tooltip: React.FC<{ text: string; visible: boolean }> = ({ text, visible }) => {
    if (!visible) return null;
    return (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-48 pointer-events-none">
            <div className="bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2 shadow-xl text-center">
                {text}
            </div>
            <div className="flex justify-center">
                <div className="w-2.5 h-2.5 bg-gray-900 rotate-45 -translate-y-1.5 shadow-sm" />
            </div>
        </div>
    );
};

// ─── Sample Image Modal ───────────────────────────────────────────────────────

const SampleImageModal: React.FC<{ image: string; label: string; tooltip: string; onClose: () => void }> = ({
    image,
    label,
    tooltip,
    onClose,
}) => (
    <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
        onClick={onClose}
    >
        <div
            className="relative bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
                <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Sample Reference</p>
                    <h3 className="text-base font-bold text-gray-900">{label}</h3>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-full transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="bg-gray-100 p-4 flex items-center justify-center">
                <img src={image} alt={`${label} sample`} className="w-full h-auto max-h-[60vh] object-contain rounded-lg shadow-sm" />
            </div>
            <div className="px-5 py-3 bg-blue-50 border-t border-blue-100 space-y-1">
                <p className="text-xs text-blue-900 font-semibold text-center">📸 What we need:</p>
                <p className="text-xs text-blue-800 text-center leading-relaxed">{tooltip}</p>
            </div>
            <button
                type="button"
                onClick={onClose}
                className="w-full py-3 bg-gray-900 hover:bg-black text-white text-sm font-medium transition-colors"
            >
                Got it
            </button>
        </div>
    </div>
);

// ─── Slot Card ────────────────────────────────────────────────────────────────

const SlotCard: React.FC<{
    slot: Slot;
    state: SlotState;
    onSlotClick: (key: SlotKey) => void;
    onRemove: (key: SlotKey) => void;
    onOpenSample: (slot: Slot) => void;
    inputRef: (el: HTMLInputElement | null) => void;
    onFileSelect: (key: SlotKey, e: ChangeEvent<HTMLInputElement>) => void;
}> = ({ slot, state, onSlotClick, onRemove, onOpenSample, inputRef, onFileSelect }) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);

    // Fill condition: user uploaded file OR pre-filled accepted URL
    const hasFile = state.file !== null || state.isAcceptedUrl;
    const hasSample = !!slot.sampleImage;

    return (
        <div
            className={`relative rounded-2xl overflow-hidden transition-all duration-200 min-h-[200px] sm:min-h-[220px] flex flex-col ${
                hasFile
                    ? state.isAcceptedUrl
                        ? "border-2 border-green-500 bg-white shadow-md"
                        : "border-2 border-blue-500 bg-white shadow-md"
                    : "border-2 border-dashed border-blue-200/80 bg-[#f8faff] hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer"
            }`}
            onClick={() => !hasFile && onSlotClick(slot.key)}
        >
            <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => onFileSelect(slot.key, e)}
                className="hidden"
            />

            {hasFile ? (
                /* ─── UPLOADED STATE ─── */
                <div className="relative w-full h-full min-h-[200px] sm:min-h-[220px]">
                    <img src={state.preview!} alt={slot.label} className="w-full h-full object-cover rounded-2xl" />

                    {state.isAcceptedUrl && (
                        <div className="absolute top-2.5 left-2.5 bg-green-600 text-white text-[10px] px-2 py-0.5 rounded font-bold z-20 shadow-md">
                            KEPT
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(slot.key);
                        }}
                        className="absolute top-2.5 right-2.5 w-7 h-7 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors shadow-md z-10"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    {hasSample && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenSample(slot);
                            }}
                            className={`absolute top-2.5 w-7 h-7 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors shadow-md z-10 ${
                                state.isAcceptedUrl ? "left-14" : "left-2.5"
                            }`}
                        >
                            <Eye className="w-3.5 h-3.5" />
                        </button>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 py-2.5 rounded-b-2xl">
                        <p className="text-white text-xs font-semibold">{slot.label}</p>
                    </div>
                </div>
            ) : (
                /* ─── EMPTY SLOT STATE ─── */
                <div className="relative w-full h-full flex flex-col items-center justify-center p-3 text-center">
                    {/* Top Right: Required / Optional Tag */}
                    <div
                        className={`absolute top-3 right-3 px-2 py-0.5 rounded-md text-[10px] font-semibold z-10 ${
                            slot.required ? "bg-red-100/90 text-red-600" : "bg-gray-200/80 text-gray-600"
                        }`}
                    >
                        {slot.required ? "Required" : "Optional"}
                    </div>

                    {/* Top Left: Info Icon with Tooltip */}
                    <div className="absolute top-3 left-3 z-20">
                        <div className="relative flex items-center justify-center">
                            <Tooltip text={slot.tooltip} visible={tooltipVisible} />
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTooltipVisible((v) => !v);
                                }}
                                onMouseEnter={() => setTooltipVisible(true)}
                                onMouseLeave={() => setTooltipVisible(false)}
                                className="w-6 h-6 bg-blue-100/80 hover:bg-blue-200 text-blue-600 rounded-full flex items-center justify-center transition-colors shadow-sm"
                            >
                                <Info className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    {/* Compact Circular Blue Container with Small Sample Image */}
                    <div
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#e8f1ff] border border-blue-100 flex items-center justify-center overflow-hidden mb-2 shadow-inner cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 transition-all"
                        onClick={(e) => {
                            e.stopPropagation(); // don't open file picker
                            if (hasSample) onOpenSample(slot);
                        }}
                        title={hasSample ? "Click to view sample" : undefined}
                    >
                        {hasSample ? (
                            <img
                                src={slot.sampleImage}
                                alt={`${slot.label} sample`}
                                className="w-10 h-10 sm:w-12 sm:h-12 object-contain pointer-events-none"
                            />
                        ) : (
                            <Camera className="w-6 h-6 text-blue-400" />
                        )}
                    </div>

                    {/* View Sample Link (Above Title) */}
                    {hasSample && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenSample(slot);
                            }}
                            className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium transition-colors mb-1 group"
                        >
                            <Eye className="w-3 h-3 text-blue-600 group-hover:text-blue-800" />
                            <span>View sample</span>
                        </button>
                    )}

                    {/* Title */}
                    <p className="text-sm text-gray-800 leading-snug">{slot.label}</p>

                    {/* Tap to Upload */}
                    <p className="text-[11px] text-gray-500 mt-0.5">Tap to upload</p>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function UploadImage({
    onFilesChange,
    acceptedSlotImages,
    onAcceptedSlotImagesChange,
}: UploadImageProps) {
    const [slotData, setSlotData] = useState<Record<SlotKey, SlotState>>(() => {
        const initial = {} as Record<SlotKey, SlotState>;
        SLOTS.forEach((s) => {
            initial[s.key] = { file: null, preview: null, isAcceptedUrl: false };
        });
        return initial;
    });

    const [error, setError] = useState<string>("");
    const [sampleModal, setSampleModal] = useState<{ image: string; label: string; tooltip: string } | null>(null);
    const inputRefs = useRef<Record<SlotKey, HTMLInputElement | null>>({} as Record<SlotKey, HTMLInputElement | null>);
    const initialized = useRef(false);

    // Map prefilled images to exact slots
    useEffect(() => {
        if (acceptedSlotImages && acceptedSlotImages.length > 0 && !initialized.current) {
            setSlotData((prev) => {
                const next = { ...prev };
                SLOTS.forEach((slot, index) => {
                    const url = acceptedSlotImages[index];
                    if (url) {
                        next[slot.key] = {
                            file: null,
                            preview: url,
                            isAcceptedUrl: true,
                        };
                    }
                });
                return next;
            });
            initialized.current = true;
        }
    }, [acceptedSlotImages]);

    const uploadedCount = Object.values(slotData).filter((s) => s.file !== null || s.isAcceptedUrl).length;

    const notifyParent = (updated: Record<SlotKey, SlotState>) => {
        // Only new files (in slot order)
        const orderedFiles = SLOTS.map((s) => updated[s.key].file).filter((f): f is File => f !== null);
        onFilesChange(orderedFiles);

        // Pre-filled URLs (nulls for missing/deleted)
        if (onAcceptedSlotImagesChange) {
            const slotUrls = SLOTS.map((s) => (updated[s.key].isAcceptedUrl ? updated[s.key].preview : null));
            onAcceptedSlotImagesChange(slotUrls);
        }
    };

    const handleSlotClick = (key: SlotKey) => {
        inputRefs.current[key]?.click();
    };

    const handleFileSelect = (key: SlotKey, e: ChangeEvent<HTMLInputElement>) => {
        setError("");
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        if (!ACCEPTED_TYPES.includes(file.type)) {
            setError("Only JPG, JPEG, PNG files are allowed.");
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError("Each file must be under 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const updated = { ...slotData, [key]: { file, preview: reader.result as string, isAcceptedUrl: false } };
            setSlotData(updated);
            notifyParent(updated);
        };
        reader.readAsDataURL(file);
    };

    const removeSlot = (key: SlotKey) => {
        const updated = { ...slotData, [key]: { file: null, preview: null, isAcceptedUrl: false } };
        setSlotData(updated);
        notifyParent(updated);
        setError("");
    };

    const openSample = (slot: Slot) => {
        if (slot.sampleImage) setSampleModal({ image: slot.sampleImage, label: slot.label, tooltip: slot.tooltip });
    };

    const progressPercent = Math.round((uploadedCount / REQUIRED_COUNT) * 100);
    const allRequiredDone = uploadedCount >= REQUIRED_COUNT;

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <p className="text-sm text-gray-800">
                    Upload <span className="font-semibold">{REQUIRED_COUNT} required</span> photos. You can add up to{" "}
                    <span className="font-semibold">{TOTAL_COUNT - REQUIRED_COUNT} more</span> ({TOTAL_COUNT} total) for
                    a clearer assessment.
                </p>
                <p className="text-xs text-gray-500">
                    Accepted: PNG, JPG, JPEG · max 5MB each ·{" "}
                    <span className="font-medium text-gray-700">
                        {uploadedCount} of {TOTAL_COUNT} uploaded
                    </span>
                </p>
            </div>

            <div className="space-y-1">
                <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Upload progress</span>
                    <span className="font-medium text-gray-700">
                        {Math.min(uploadedCount, REQUIRED_COUNT)}/{REQUIRED_COUNT} required
                    </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                        className={`h-2 rounded-full transition-all duration-500 ${allRequiredDone ? "bg-green-500" : "bg-blue-500"}`}
                        style={{ width: `${Math.min(progressPercent, 100)}%` }}
                    />
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 text-sm text-center">⚠️ {error}</p>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {SLOTS.map((slot) => (
                    <SlotCard
                        key={slot.key}
                        slot={slot}
                        state={slotData[slot.key]}
                        onSlotClick={handleSlotClick}
                        onRemove={removeSlot}
                        onOpenSample={openSample}
                        inputRef={(el) => (inputRefs.current[slot.key] = el)}
                        onFileSelect={handleFileSelect}
                    />
                ))}
            </div>

            {!allRequiredDone ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-yellow-800 text-xs text-center">
                        📷 Please upload all {REQUIRED_COUNT} required photos to continue ({uploadedCount}/
                        {REQUIRED_COUNT})
                    </p>
                </div>
            ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-700 text-xs text-center font-medium">✅ All required photos uploaded</p>
                </div>
            )}

            {sampleModal && (
                <SampleImageModal
                    image={sampleModal.image}
                    label={sampleModal.label}
                    tooltip={sampleModal.tooltip}
                    onClose={() => setSampleModal(null)}
                />
            )}
        </div>
    );
}